import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UserProfile } from '../../../../../../components';
import { organisationApi, User } from '../../../../../../services';
import { useAppDispatch } from '../../../../../hooks';
import { AutoComplete } from '../../../../AutoComplete';
import { Avatar } from '../../../../Avatar';
import { CloseWrapper, useBlockToolInputFocus } from '../../helpers';

import classes from './UserNodeView.module.scss';

export const UserNodeView: FunctionComponent<NodeViewProps> = ({
  editor,
  node,
  getPos,
  updateAttributes,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { onBlockInputFocus } = useBlockToolInputFocus({ editor, getPos });

  const itemTemplate = useCallback(({ avatar, screenName }: User) => {
    return (
      <>
        <Avatar url={avatar?.url} />
        <div className={classes['user-name']}>{screenName}</div>
      </>
    );
  }, []);

  const fetchQuery = useCallback(
    (query: string, page?: number) => {
      return dispatch(
        organisationApi.endpoints.organisationUsers.initiate({ page, filter: { query } })
      );
    },
    [dispatch]
  );

  const onChange = useCallback(
    (users: User[]) => updateAttributes({ user: users.slice(-1).pop() }),
    [updateAttributes]
  );

  const content = useMemo(() => {
    const { user } = node.attrs;

    if (user) {
      return <UserProfile user={user} />;
    }

    return (
      <AutoComplete
        propToIdentify={'id'}
        propToDisplay={'id'}
        fetchQuery={fetchQuery}
        itemTemplate={itemTemplate}
        value={[user].filter(Boolean)}
        onChange={onChange}
        onFocus={onBlockInputFocus}
        placeholder={t('richTextEditor.user-config-placeholder')}
      />
    );
  }, [fetchQuery, itemTemplate, node.attrs, onBlockInputFocus, onChange, t]);

  return (
    <NodeViewWrapper>
      <CloseWrapper editor={editor} node={node} getPos={getPos}>
        {content}
      </CloseWrapper>
    </NodeViewWrapper>
  );
};
